/**
 * Own-account transfer (07-wallets "Own-account transfers with fees"): money between two of the TC's own bank, cash and
 * e-wallet accounts. The sending bank's charge is added on top; the receiving account's fee is taken from what arrives.
 * Each account's fee preset fills its fee in (never posted on its own) and can be changed or cleared here. A cash account
 * carries no fee. One transfer id per open dialog (a new one after an error) so a double click posts once.
 *
 * Laid out like the posting: each account is a panel written as its own statement (the lines that hit it, its balance
 * now and after), and a fee sits in the panel of the account that pays it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem,
  Select, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, Alert, Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SwapVert as SwapIcon } from '@mui/icons-material';
import WalletService from '../../services/wallet.service';
import { SavingOverlay } from '../../components/accounting';
import { serverMessage } from '../../utils/apiConvert';

const fs = { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' };
const NUM = { fontVariantNumeric: 'tabular-nums' };
const TYPE_LABEL = { OPERATIONS: 'Bank', NGENIUS_SETTLEMENT: 'N-Genius', CASH: 'Cash', E_WALLET: 'E-wallet' };
const today = () => new Date().toLocaleDateString('en-CA');
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : ((r % 4) + 8)).toString(16);
  }));
const money = (n) => Number(n || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Money in whole cents and a fee value in 1/10,000ths, so the rounding matches the server's HALF_UP exactly
// (floating point gets exact half-cents wrong: 2.5% of 87.00 must be 2.18).
const cents = (n) => Math.round((Number(n) || 0) * 100);
const tenThousandths = (n) => Math.round((Number(n) || 0) * 10000);

// The fee in AED: FLAT as entered, PERCENT of the amount - rounded half-up to 2 decimals like the server.
export const feeAmount = (fee, amount) => {
  const v = Number(fee?.value);
  if (!fee || fee.value === '' || !(v > 0)) return 0;
  const value = tenThousandths(v);
  return fee.type === 'PERCENT'
    ? Math.round((cents(amount) * value) / 1000000) / 100
    : Math.round(value / 100) / 100;
};

const presetFee = (account, direction) => {
  if (!account || account.accountType === 'CASH') return { type: 'FLAT', value: '' };
  const type = direction === 'out' ? account.transferOutFeeType : account.transferInFeeType;
  const value = direction === 'out' ? account.transferOutFeeValue : account.transferInFeeValue;
  return type && value != null && Number(value) > 0 ? { type, value: String(Number(value)), preset: true } : { type: 'FLAT', value: '' };
};

// A line of money on one account: the transfer itself, out (−) or in (+).
const MoneyLine = ({ label, value, sign }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, py: 0.5 }}>
    <Typography sx={{ fontSize: fs.sm }}>{label}</Typography>
    <Typography sx={{ ...NUM, fontSize: fs.sm, fontWeight: 600, whiteSpace: 'nowrap', color: sign === '+' ? 'success.main' : 'error.main' }}>
      {sign === '+' ? '+ ' : '− '}{money(value)}
    </Typography>
  </Box>
);

// The fee of the account that pays it: AED or % and the value, the worked-out amount on the right.
const FeeLine = ({ label, fee, amount, onChange }) => {
  const value = feeAmount(fee, amount);
  const tooBig = fee.type === 'PERCENT' && Number(fee.value) > 100;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.5 }}>
      <Typography sx={{ fontSize: fs.sm, flex: '1 1 120px', color: 'text.primary' }}>{label}</Typography>
      <ToggleButtonGroup exclusive size="small" value={fee.type} onChange={(e, v) => { if (v) onChange({ ...fee, type: v }); }}
        aria-label={`${label}: amount or percentage`}
        sx={{ '& .MuiToggleButton-root': { height: 30, px: 1.25, fontSize: fs.xs, textTransform: 'none' } }}>
        <ToggleButton value="FLAT">AED</ToggleButton>
        <ToggleButton value="PERCENT">%</ToggleButton>
      </ToggleButtonGroup>
      <TextField size="small" type="number" value={fee.value} placeholder="None" error={tooBig}
        inputProps={{ min: 0, step: '0.01', 'aria-label': label, ...(fee.type === 'PERCENT' ? { max: 100 } : {}), style: NUM }}
        sx={{ width: 96, '& .MuiInputBase-input': { py: 0.75, fontSize: fs.sm } }}
        onChange={(e) => onChange({ ...fee, value: e.target.value, preset: false })} />
      {fee.preset && (
        <Tooltip title="Filled in from this account's transfer fee. Remove it for this transfer only.">
          <Chip label="Preset" size="small" variant="outlined" onDelete={() => onChange({ type: 'FLAT', value: '' })}
            sx={{ height: 22, fontSize: '0.68rem' }} />
        </Tooltip>
      )}
      <Typography sx={{ ...NUM, fontSize: fs.sm, minWidth: 84, textAlign: 'right', ml: 'auto', whiteSpace: 'nowrap',
        color: value > 0 ? 'error.main' : 'text.disabled' }}>
        {value > 0 ? `− ${money(value)}` : '—'}
      </Typography>
    </Box>
  );
};

// One end of the transfer, written like that account's statement. Its colour says which way the money goes: a faint
// wash and outline of red (money out) or green (money in), with a solid edge; plain white until an account is picked.
const AccountPanel = ({ role, tone, selected, before, after, children }) => (
  <Box sx={(t) => ({
    pl: 2, pr: 1.5, py: 1.25, borderRadius: '0 8px 8px 0',
    border: 1, borderLeft: 3,
    borderColor: selected ? alpha(t.palette[tone].main, 0.28) : t.palette.divider,
    borderLeftColor: selected ? t.palette[tone].main : t.palette.divider,
    bgcolor: selected ? alpha(t.palette[tone].main, t.palette.mode === 'dark' ? 0.08 : 0.04) : 'background.paper',
    transition: 'background-color 160ms ease, border-color 160ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  })}>
    <Typography sx={{ fontSize: fs.xs, fontWeight: 700, mb: 0.75, color: selected ? `${tone}.main` : 'text.secondary' }}>{role}</Typography>
    {children}
    {selected && (
      <Box sx={(t) => ({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mt: 0.75, pt: 0.75,
        borderTop: 1, borderColor: alpha(t.palette[tone].main, 0.2) })}>
        <Typography sx={{ fontSize: fs.xs, color: 'text.primary' }}>Balance now, then after</Typography>
        <Typography sx={{ ...NUM, fontSize: fs.sm, whiteSpace: 'nowrap', color: 'text.primary' }}>
          <Box component="span" sx={{ mr: 1.5 }}>{money(before)}</Box>
          <Box component="span" sx={{ fontWeight: 700, color: after < 0 ? 'error.main' : 'text.primary' }}>{money(after)}</Box>
        </Typography>
      </Box>
    )}
  </Box>
);

const BankTransferDialog = ({ open, onClose, accounts, initialFromId, onDone }) => {
  const usable = useMemo(() => (accounts || []).filter((a) => a.isActive), [accounts]);
  const byId = (id) => usable.find((a) => a.id === id);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [reference, setReference] = useState('');
  const [senderFee, setSenderFee] = useState({ type: 'FLAT', value: '' });
  const [receiverFee, setReceiverFee] = useState({ type: 'FLAT', value: '' });
  const [transferId, setTransferId] = useState(newId());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const from = initialFromId && byId(initialFromId) ? initialFromId : '';
    setFromId(from); setToId(''); setAmount(''); setDate(today()); setReference('');
    setSenderFee(presetFee(byId(from), 'out')); setReceiverFee({ type: 'FLAT', value: '' });
    setTransferId(newId()); setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFromId]);

  const from = byId(fromId);
  const to = byId(toId);
  const pickFrom = (id) => { setFromId(id); setSenderFee(presetFee(byId(id), 'out')); if (id === toId) { setToId(''); setReceiverFee({ type: 'FLAT', value: '' }); } };
  const pickTo = (id) => { setToId(id); setReceiverFee(presetFee(byId(id), 'in')); };
  const swap = () => {
    const f = fromId; const t = toId;
    setFromId(t); setToId(f);
    setSenderFee(presetFee(byId(t), 'out')); setReceiverFee(presetFee(byId(f), 'in'));
  };

  const amt = Number(amount) || 0;
  const sFee = from?.accountType === 'CASH' ? 0 : feeAmount(senderFee, amt);
  const rFee = to?.accountType === 'CASH' ? 0 : feeAmount(receiverFee, amt);
  const leaves = (cents(amt) + cents(sFee)) / 100;
  const arrives = (cents(amt) - cents(rFee)) / 100;
  const charges = (cents(sFee) + cents(rFee)) / 100;
  const fromBalance = Number(from?.balance || 0);
  const toBalance = Number(to?.balance || 0);
  const short = from && leaves > fromBalance;
  const receiverTooBig = amt > 0 && rFee >= amt;
  const percentTooBig = [senderFee, receiverFee].some((f) => f.type === 'PERCENT' && Number(f.value) > 100);
  const valid = from && to && fromId !== toId && amt > 0 && !receiverTooBig && !short && !percentTooBig
    && date && date <= today();

  const submit = async () => {
    setBusy(true); setError(null);
    const feeBody = (fee, value) => (value > 0 ? { type: fee.type, value: Number(fee.value) } : null);
    try {
      const entry = await WalletService.transfer({
        transferId, fromAccountId: fromId, toAccountId: toId, amount: amt, transferDate: date,
        reference: reference.trim() || null, senderFee: feeBody(senderFee, sFee), receiverFee: feeBody(receiverFee, rFee),
      });
      onDone?.(entry);
    } catch (err) {
      setError(serverMessage(err, 'The transfer failed'));
      setTransferId(newId());
    } finally {
      setBusy(false);
    }
  };

  const option = (a) => (
    <MenuItem key={a.id} value={a.id} sx={{ fontSize: fs.sm }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Chip label={TYPE_LABEL[a.accountType] || a.accountType} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {a.accountName}{a.bankName && a.accountType !== 'CASH' ? `, ${a.bankName}` : ''}
        </Box>
        <Typography component="span" sx={{ ...NUM, fontSize: fs.xs, color: 'text.secondary' }}>{money(a.balance)}</Typography>
      </Box>
    </MenuItem>
  );

  const picker = (label, value, onPick, list) => (
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select value={value} label={label} onChange={(e) => onPick(e.target.value)}
        renderValue={(id) => byId(id)?.accountName || ''} sx={{ bgcolor: 'background.paper' }}>
        {list.map(option)}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { position: 'relative' } }}>
      <SavingOverlay open={busy} />
      <DialogTitle sx={{ fontSize: fs.md, fontWeight: 600, pb: 0.5 }}>Transfer between accounts</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: fs.sm }} onClose={() => setError(null)}>{error}</Alert>}

        <TextField fullWidth type="number" label="Amount transferred (before fees)" value={amount} autoFocus
          helperText="What the receiving side should get before its own fee" onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0, step: '0.01', style: { ...NUM, fontSize: '1.5rem', fontWeight: 600 } }}
          InputProps={{ startAdornment: <Typography sx={{ color: 'text.secondary', mr: 1, fontSize: fs.md }}>AED</Typography> }}
          sx={{ mt: 1.5 }} />
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
          <TextField size="small" type="date" label="Date" value={date} InputLabelProps={{ shrink: true }}
            inputProps={{ max: today() }} onChange={(e) => setDate(e.target.value)} sx={{ flex: '1 1 150px' }} />
          <TextField size="small" label="Reference" placeholder="The bank's transfer number" value={reference}
            onChange={(e) => setReference(e.target.value)} sx={{ flex: '2 1 200px' }} />
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <AccountPanel role="From" tone="error" selected={Boolean(from)} before={fromBalance}
            after={(cents(fromBalance) - cents(leaves)) / 100}>
            {picker('Account the money leaves', fromId, pickFrom, usable)}
            {from && amt > 0 && (
              <Box sx={{ mt: 1 }}><MoneyLine label={to ? `Transfer to ${to.accountName}` : 'Transfer'} value={amt} sign="-" /></Box>
            )}
            {from && from.accountType !== 'CASH' && (
              <FeeLine label={`Charged by ${from.accountName}`} fee={senderFee} amount={amt} onChange={setSenderFee} />
            )}
          </AccountPanel>

          {/* the flow between the two ends: the line and what leaves / arrives on the left, swap in the centre */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', minHeight: 48 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', minWidth: 0 }}>
              {/* one line from the red edge to the green one once both ends are picked */}
              <Box sx={(t) => ({ width: 3, flexShrink: 0, alignSelf: 'stretch',
                background: from && to ? `linear-gradient(${t.palette.error.main}, ${t.palette.success.main})` : t.palette.divider })} />
              {from && to && amt > 0 && (
                <Box sx={(t) => ({ ml: 1.5, my: 0.75, px: 1.5, py: 0.5, borderRadius: 999, border: 1,
                  borderColor: alpha(t.palette.primary.main, 0.3), bgcolor: alpha(t.palette.primary.main, 0.08) })}>
                  <Typography sx={{ ...NUM, fontSize: fs.sm, color: 'text.primary' }}>
                    <Box component="b" sx={{ color: 'error.main' }}>{money(leaves)}</Box> leaves,{' '}
                    <Box component="b" sx={{ color: 'success.main' }}>{money(arrives)}</Box> arrives
                  </Typography>
                </Box>
              )}
            </Box>
            <Tooltip title="Swap From and To">
              <span>
                <IconButton size="small" onClick={swap} disabled={!fromId || !toId} aria-label="Swap From and To"
                  sx={{ mx: 1, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                  <SwapIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Box />
          </Box>

          <AccountPanel role="To" tone="success" selected={Boolean(to)} before={toBalance}
            after={(cents(toBalance) + cents(arrives)) / 100}>
            {picker('Account the money reaches', toId, pickTo, usable.filter((a) => a.id !== fromId))}
            {to && amt > 0 && (
              <Box sx={{ mt: 1 }}><MoneyLine label={from ? `Transfer from ${from.accountName}` : 'Transfer'} value={amt} sign="+" /></Box>
            )}
            {to && to.accountType !== 'CASH' && (
              <FeeLine label={`Deducted by ${to.accountName}`} fee={receiverFee} amount={amt} onChange={setReceiverFee} />
            )}
          </AccountPanel>
        </Box>

        {charges > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, px: 0.5 }}>
            <Typography sx={{ fontSize: fs.sm, color: 'text.secondary' }}>Booked to Bank Charges</Typography>
            <Typography sx={{ ...NUM, fontSize: fs.sm, fontWeight: 600 }}>{money(charges)}</Typography>
          </Box>
        )}

        <Box role="status" aria-live="polite" sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {short && (
            <Alert severity="error" sx={{ fontSize: fs.xs, py: 0 }}>
              {from.accountName} has {money(fromBalance)}. This transfer needs {money(leaves)}.
            </Alert>
          )}
          {receiverTooBig && <Alert severity="error" sx={{ fontSize: fs.xs, py: 0 }}>The receiving fee must be less than the amount.</Alert>}
          {!receiverTooBig && amt > 0 && sFee >= amt && (
            <Alert severity="warning" sx={{ fontSize: fs.xs, py: 0 }}>The charge is as large as the amount. Check it before recording.</Alert>
          )}
          {usable.length < 2 && <Alert severity="info" sx={{ fontSize: fs.xs, py: 0 }}>Add a second active account to transfer between.</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button size="small" variant="contained" onClick={submit} disabled={busy || !valid}>
          {from && to && amt > 0 ? `Record transfer of ${money(amt)}` : 'Record transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankTransferDialog;
